import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { extractMarketingBriefFromFile } from '../../../API/agent';
import { OFFICE_UPLOAD_ACCEPT, OFFICE_UPLOAD_ALLOWED_EXTENSIONS, SUPPORTED_UPLOAD_FORMATS_LABEL } from '../uploadFileConfig';

const useStyles = makeStyles(() => ({
  mainContainer: {
    width: '100%',
    maxWidth: '650px',
    margin: '0 auto 30px auto',
  },
  actions: {
    marginTop: '16px',
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  helper: {
    marginTop: '10px',
  },
}));

const MAX_SIZE_BYTES = 25 * 1024 * 1024;

const toRtbText = value => {
  if (!Array.isArray(value) || !value.length) return '';
  return value.map(item => `• ${item}`).join('\n');
};

const toCurrency = currency => {
  if (!currency) return 'usd';
  const normalized = String(currency).toLowerCase();
  if (['usd', 'eur', 'gbp'].includes(normalized)) return normalized;
  return 'usd';
};

const BriefUpload = props => {
  const classes = useStyles();
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const extractionStatus = props.surveyAnswersState?.Post_Brief_Extraction_Status;
  const requestIdRef = useRef(0);
  const currentBriefSourceRef = useRef(props.surveyAnswersState?.Post_Brief_Source);

  useEffect(() => {
    currentBriefSourceRef.current = props.surveyAnswersState?.Post_Brief_Source;
  }, [props.surveyAnswersState?.Post_Brief_Source]);

  const extracted = useMemo(() => extractionStatus === 'completed', [extractionStatus]);

  const validateFile = file => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!OFFICE_UPLOAD_ALLOWED_EXTENSIONS.includes(extension)) return 'Only PDF, DOC, DOCX, PPT, and PPTX files are supported.';
    if (file.size > MAX_SIZE_BYTES) return 'File is too large. Maximum allowed size is 25 MB.';
    return '';
  };

  const onUpload = async event => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    setError('');
    props.handleSurveyAnswers('processing', 'Post_Brief_Extraction_Status');
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    try {
      const result = await extractMarketingBriefFromFile({ file, surveyId: props.surveyAnswersState?.id });
      const isLatestRequest = requestIdRef.current === requestId;
      const briefSourceStillUpload = !currentBriefSourceRef.current || currentBriefSourceRef.current === 'upload';

      if (!isLatestRequest || !briefSourceStillUpload) {
        return;
      }

      if (result?.err) {
        throw new Error(result?.errMessage || 'Extraction failed.');
      }

      const extraction = result?.result || {};
      const budget = extraction?.brief?.budget;

      props.handleSurveyAnswers('upload', 'Post_Brief_Source');
      props.handleSurveyAnswers(extraction?.brief?.background || '', 'Post_Background');
      props.handleSurveyAnswers(extraction?.brief?.objective || '', 'Post_Objective');
      props.handleSurveyAnswers(extraction?.brief?.targetAudience || '', 'Post_Target_Audience');
      props.handleSurveyAnswers(extraction?.brief?.customerInsight || '', 'Post_Customer_Insight');
      props.handleSurveyAnswers(extraction?.brief?.keyMessage || '', 'Post_Key_Message');
      props.handleSurveyAnswers(toRtbText(extraction?.brief?.reasonsToBelieve), 'Post_RTB');
      props.handleSurveyAnswers(extraction?.brief?.creativeDirection || '', 'Post_Creative_Direction');
      props.handleSurveyAnswers(
        budget
          ? {
              amount: budget.amount || '',
              currency: toCurrency(budget.currency),
            }
          : {
              amount: '',
              currency: 'usd',
            },
        'Post_Budget',
      );

      props.handleSurveyAnswers(extraction?.briefFileName || file.name, 'Post_Brief_File_Name');
      props.handleSurveyAnswers(extraction?.briefFileUrl || '', 'Post_Brief_File_Url');
      props.handleSurveyAnswers('completed', 'Post_Brief_Extraction_Status');
      props.handleSurveyAnswers(extraction?.brief?.missingFields || [], 'Post_Brief_Extraction_Missing_Fields');
      props.handleSurveyAnswers(extraction?.brief?.inferredFields || [], 'Post_Brief_Extraction_Inferred_Fields');
      props.handleSurveyAnswers(extraction?.rawResponse || null, 'Post_Brief_Extraction_Raw_Response');
    } catch (err) {
      if (requestIdRef.current === requestId && (!currentBriefSourceRef.current || currentBriefSourceRef.current === 'upload')) {
        setError(err.message || 'Extraction failed.');
        props.handleSurveyAnswers('failed', 'Post_Brief_Extraction_Status');
      }
    } finally {
      if (requestIdRef.current === requestId) {
        setUploading(false);
      }
      event.target.value = '';
    }
  };

  return (
    <Box className={classes.mainContainer}>
      <Typography variant="h3">Upload campaign brief document</Typography>
      <Typography variant="body1" className={classes.helper}>
        {SUPPORTED_UPLOAD_FORMATS_LABEL}
      </Typography>
      <Box className={classes.actions}>
        <Button variant="contained" component="label" disabled={uploading} style={{ background: '#19989B', color: '#fff' }}>
          {uploading ? (
            <>
              <CircularProgress size={16} style={{ color: '#fff', marginRight: 8 }} /> Extracting...
            </>
          ) : (
            'Choose file'
          )}
          <input hidden type="file" accept={OFFICE_UPLOAD_ACCEPT} onChange={onUpload} />
        </Button>
      </Box>
      {extracted ? <Typography style={{ marginTop: 12 }}>Extraction completed. Review and edit the fields in the next steps.</Typography> : null}
      {error ? <Typography style={{ marginTop: 12, color: '#D32F2F' }}>{error} Please retry with another file or go Back to choose a different brief option.</Typography> : null}
    </Box>
  );
};

export default BriefUpload;
